import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { drobek, DrobekError, type data } from 'drobek';
import confetti from 'canvas-confetti';
import './styles.css';

// ───────────────────────── data ─────────────────────────
const NAMES = ['Kamča', 'Lucík', 'Sjůzn', 'Katy'] as const;
type Name = (typeof NAMES)[number];
/** Oslovení (5. pád). */
const VOCATIVE: Record<Name, string> = { 'Kamča': 'Kamčo', 'Lucík': 'Lucíku', 'Sjůzn': 'Sjůzn', 'Katy': 'Katy' };

/** Přihláška: klíče zamčené heslem, adresa + přání (`self`) zamčené tím samým heslem. */
type Member = { name: Name; pub: string; salt: string; iv: string; priv: string; self: string; selfIv: string };
/** Losování: pro každou jeden balíček zamčený jejím klíčem. */
type Draw = Record<Name, string>;
/** Schránka: adresa + přání majitelky zamčené klíčem, který zná jen její dárkyně. */
type Box = { name: Name; iv: string; data: string };

type Sealed = { to: Name; giveKey: string; myKey: string };
type Profile = { address: string; wish: string };
type MemberRow = data.Doc<Member>;
type DrawRow = data.Doc<Draw>;
type BoxRow = data.Doc<Box>;
type Session = { name: Name; priv: CryptoKey; aes: CryptoKey };

const members = drobek.data.collection<Member>('holky');
const draws = drobek.data.collection<Draw>('los');
const boxes = drobek.data.collection<Box>('schranka');

const BUDGET = '300 Kč';
const DEADLINE = '15. 12.';

// ───────────────────────── crypto ─────────────────────────
const enc = new TextEncoder();
const dec = new TextDecoder();
const b64 = (buf: ArrayBuffer) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const unb64 = (s: string): ArrayBuffer => Uint8Array.from(atob(s), (c) => c.charCodeAt(0)).buffer as ArrayBuffer;
const RSA = { name: 'RSA-OAEP', hash: 'SHA-256' } as const;

async function deriveKey(password: string, salt: ArrayBuffer) {
  const base = await crypto.subtle.importKey('raw', enc.encode(password), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: 300_000, hash: 'SHA-256' },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

async function aesEncrypt(key: CryptoKey, text: string) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(text));
  return { iv: b64(iv.buffer as ArrayBuffer), data: b64(data) };
}

async function aesDecrypt(key: CryptoKey, iv: string, data: string) {
  return dec.decode(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: unb64(iv) }, key, unb64(data)));
}

async function randomKeyB64() {
  const k = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
  return b64(await crypto.subtle.exportKey('raw', k));
}

function importKeyB64(raw: string) {
  return crypto.subtle.importKey('raw', unb64(raw), 'AES-GCM', false, ['encrypt', 'decrypt']);
}

/** Nový pár klíčů + adresa a přání; všechno zamčené heslem, server uvidí jen šifry. */
async function makeIdentity(password: string, profile: Profile) {
  const kp = await crypto.subtle.generateKey(
    { ...RSA, modulusLength: 2048, publicExponent: new Uint8Array([1, 0, 1]) },
    true,
    ['encrypt', 'decrypt'],
  );
  const pub = JSON.stringify(await crypto.subtle.exportKey('jwk', kp.publicKey));
  const pkcs8 = await crypto.subtle.exportKey('pkcs8', kp.privateKey);
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const aes = await deriveKey(password, salt.buffer as ArrayBuffer);
  const wrapped = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aes, pkcs8);
  const self = await aesEncrypt(aes, JSON.stringify(profile));
  const fields: Omit<Member, 'name'> = {
    pub, salt: b64(salt.buffer as ArrayBuffer), iv: b64(iv.buffer as ArrayBuffer), priv: b64(wrapped), self: self.data, selfIv: self.iv,
  };
  return { fields, privateKey: kp.privateKey, aes };
}

async function unlockIdentity(m: Member, password: string): Promise<{ priv: CryptoKey; aes: CryptoKey }> {
  const aes = await deriveKey(password, unb64(m.salt));
  const pkcs8 = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: unb64(m.iv) }, aes, unb64(m.priv));
  const priv = await crypto.subtle.importKey('pkcs8', pkcs8, RSA, false, ['decrypt']);
  return { priv, aes };
}

async function sealFor(pubJwk: string, text: string) {
  const key = await crypto.subtle.importKey('jwk', JSON.parse(pubJwk), RSA, false, ['encrypt']);
  return b64(await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, key, enc.encode(text)));
}

async function openSealed(priv: CryptoKey, ciphertext: string) {
  return dec.decode(await crypto.subtle.decrypt({ name: 'RSA-OAEP' }, priv, unb64(ciphertext)));
}

/** Náhodné přiřazení, ve kterém nikdo nedostane sám sebe. */
function derange(list: readonly Name[]): Record<Name, Name> {
  for (;;) {
    const arr = [...list];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = crypto.getRandomValues(new Uint32Array(1))[0] % (i + 1);
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    if (list.every((n, i) => n !== arr[i])) {
      return Object.fromEntries(list.map((n, i) => [n, arr[i]])) as Record<Name, Name>;
    }
  }
}

function describeError(e: unknown): string {
  if (e instanceof DrobekError) {
    if (e.code === 'pending_confirmation' || e.code === 'not_found') return 'Appka ještě není úplně zapnutá. Zkus to prosím za chvíli.';
    if (e.code === 'validation_failed') return 'Některý text je moc dlouhý. Zkus ho trochu zkrátit.';
    if (e.code === 'rate_limited') return 'Moc rychle za sebou. Zkus to za chvilku.';
    return e.message;
  }
  return e instanceof Error ? e.message : 'Něco se pokazilo.';
}

/** Seznam z kolekce: načte se hned, pak každých pár sekund a při návratu do aplikace. Žádné živé odběry (mají limit na síť). */
function usePolledList<T extends object>(col: data.Collection<T>, onError: (msg: string) => void, everyMs = 8000) {
  const [list, setList] = useState<data.Doc<T>[] | null>(null);
  const load = useCallback(
    () => col.list({ sort: '_created_at', dir: 'asc', limit: 100 }).then((p) => setList(p.records), (e) => onError(describeError(e))),
    [],
  );
  useEffect(() => {
    void load();
    const tick = () => { if (document.visibilityState === 'visible') void load(); };
    const timer = setInterval(tick, everyMs);
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('focus', tick);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', tick); window.removeEventListener('focus', tick); };
  }, [load]);
  return [list, load] as const;
}

// ───────────────────────── sníh ─────────────────────────
function Snow() {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let w = 0;
    let h = 0;
    let raf = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    type Flake = { x: number; y: number; r: number; s: number; d: number; o: number };
    let flakes: Flake[] = [];
    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.round((w * h) / 9000);
      flakes = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 0.8 + Math.random() * 2.6,
        s: 0.3 + Math.random() * 0.9,
        d: Math.random() * Math.PI * 2,
        o: 0.35 + Math.random() * 0.55,
      }));
    };
    let t = 0;
    const tick = () => {
      t += 0.01;
      ctx.clearRect(0, 0, w, h);
      for (const f of flakes) {
        f.y += f.s;
        f.x += Math.sin(t + f.d) * 0.4 + Math.sin(t * 0.3) * 0.15;
        if (f.y > h + 5) { f.y = -5; f.x = Math.random() * w; }
        if (f.x > w + 5) f.x = -5;
        if (f.x < -5) f.x = w + 5;
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,250,240,${f.o})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(tick);
    };
    resize();
    tick();
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={ref} className="snow" aria-hidden="true" />;
}

// ───────────────────────── UI kousky ─────────────────────────
function Lights() {
  return (
    <div className="lights" aria-hidden="true">
      {Array.from({ length: 14 }, (_, i) => <i key={i} style={{ animationDelay: `${(i % 5) * 0.37}s` }} />)}
    </div>
  );
}

function Roster({ list }: { list: MemberRow[] }) {
  const done = new Set(list.map((m) => m.name));
  return (
    <ul className="roster" aria-label="Které už jsou přihlášené">
      {NAMES.map((n) => (
        <li key={n} className={done.has(n) ? 'in' : ''}>
          <span className="dot" aria-hidden="true">{done.has(n) ? '✓' : '·'}</span>
          {n}
        </li>
      ))}
    </ul>
  );
}

function Gift({ state, onClick }: { state: 'idle' | 'shake' | 'open'; onClick?: () => void }) {
  return (
    <button type="button" className={`gift ${state}`} onClick={onClick} disabled={state !== 'idle'} aria-label="Rozbalit dárek">
      <span className="glow" aria-hidden="true" />
      <span className="lid"><span className="bow" /></span>
      <span className="box"><span className="ribbon" /></span>
      <span className="sparkles" aria-hidden="true">
        {Array.from({ length: 12 }, (_, i) => <i key={i} style={{ ['--i' as string]: i }} />)}
      </span>
    </button>
  );
}

function Card({ title, children, back, kicker }: { title: string; children: ReactNode; back?: () => void; kicker?: string }) {
  return (
    <section className="card">
      {back && <button type="button" className="back" onClick={back}>← zpět</button>}
      {kicker && <p className="kicker">{kicker}</p>}
      <h2>{title}</h2>
      {children}
    </section>
  );
}

// ───────────────────────── appka ─────────────────────────
type Stage = 'landing' | 'who' | 'register' | 'login' | 'home';

function App() {
  const [stage, setStage] = useState<Stage>('landing');
  const [tab, setTab] = useState<'app' | 'help'>('app');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [list, reloadMembers] = usePolledList(members, setError);
  const [drawList, reloadDraws] = usePolledList(draws, setError);
  const [boxList, reloadBoxes] = usePolledList(boxes, setError);
  const draw: DrawRow | null | undefined = drawList ? drawList[0] ?? null : undefined;
  const [me, setMe] = useState<Name | null>(() => {
    try { return (localStorage.getItem('jezisek.me') as Name) || null; } catch { return null; }
  });
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => { window.scrollTo({ top: 0 }); }, [stage, tab]);

  const mine = me ? list?.find((m) => m.name === me) ?? null : null;

  function pick(n: Name) {
    setMe(n);
    try { localStorage.setItem('jezisek.me', n); } catch { /* nevadí */ }
    setError('');
    setStage(list?.some((m) => m.name === n) ? 'login' : 'register');
  }

  async function register(form: Profile & { password: string }) {
    if (!me) return;
    setBusy('Chviličku, ukládám…');
    setError('');
    try {
      const fresh = await members.list({ filter: { name: me }, limit: 1 });
      if (fresh.records.length) throw new Error(`${me} už je přihlášená. Zadej své heslo.`);
      const id = await makeIdentity(form.password, { address: form.address.trim(), wish: form.wish.trim() });
      await members.create({ name: me, ...id.fields });
      await reloadMembers();
      setSession({ name: me, priv: id.privateKey, aes: id.aes });
      setStage('home');
    } catch (e) {
      setError(describeError(e));
      if (e instanceof Error && e.message.includes('už je přihlášená')) setStage('login');
    } finally {
      setBusy('');
    }
  }

  async function login(password: string) {
    if (!mine) return;
    setBusy('Odemykám…');
    setError('');
    try {
      const keys = await unlockIdentity(mine, password);
      setSession({ name: mine.name, ...keys });
      setStage('home');
    } catch {
      setError('Tohle heslo nesedí. Zkus to znovu.');
    } finally {
      setBusy('');
    }
  }

  async function runDraw() {
    if (!list || list.length < NAMES.length) return;
    setBusy('Losuju…');
    setError('');
    try {
      const existing = await draws.list({ sort: '_created_at', dir: 'asc', limit: 1 });
      if (existing.records.length) { await reloadDraws(); return; }
      const pairs = derange(NAMES);
      const boxKeys = {} as Record<Name, string>;
      for (const n of NAMES) boxKeys[n] = await randomKeyB64();
      const sealed = {} as Draw;
      for (const giver of NAMES) {
        const m = list.find((x) => x.name === giver)!;
        const payload: Sealed = { to: pairs[giver], giveKey: boxKeys[pairs[giver]], myKey: boxKeys[giver] };
        sealed[giver] = await sealFor(m.pub, JSON.stringify(payload));
      }
      await draws.create(sealed);
      await reloadDraws();
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy('');
    }
  }

  function logout() {
    setSession(null);
    setStage('landing');
  }

  return (
    <>
      <Snow />
      <Lights />
      <main className="page">
        <nav className="tabs" aria-label="Záložky">
          <button type="button" className={tab === 'app' ? 'on' : ''} onClick={() => setTab('app')} aria-current={tab === 'app' ? 'page' : undefined}>🎁 Ježíšek</button>
          <button type="button" className={tab === 'help' ? 'on' : ''} onClick={() => setTab('help')} aria-current={tab === 'help' ? 'page' : undefined}>Jak to funguje</button>
        </nav>
        {tab === 'help' && <Help onBack={() => setTab('app')} />}
        {tab === 'app' && stage === 'landing' && (
          <Landing list={list} draw={draw} boxList={boxList ?? []} onStart={() => { setError(''); setStage('who'); }} />
        )}
        {tab === 'app' && stage === 'who' && (
          <Card title="Která jsi?" back={() => setStage('landing')}>
            <div className="who">
              {NAMES.map((n) => {
                const inside = list?.some((m) => m.name === n);
                return (
                  <button key={n} type="button" className={`name-btn ${inside ? 'in' : ''}`} onClick={() => pick(n)}>
                    <span className="big">{n}</span>
                    <span className="small">{inside ? 'už přihlášená · zadej heslo' : 'ještě nepřihlášená'}</span>
                  </button>
                );
              })}
            </div>
          </Card>
        )}
        {tab === 'app' && stage === 'register' && me && (
          <RegisterForm name={me} busy={busy} error={error} onSubmit={register} back={() => setStage('who')} />
        )}
        {tab === 'app' && stage === 'login' && me && (
          <LoginForm name={me} busy={busy} error={error} onSubmit={login} back={() => setStage('who')} />
        )}
        {tab === 'app' && stage === 'home' && session && (
          <Home session={session} mine={mine} list={list ?? []} draw={draw} boxList={boxList ?? []} refreshBoxes={reloadBoxes} busy={busy} error={error} onDraw={runDraw} onLogout={logout} />
        )}
        {tab === 'app' && stage !== 'register' && stage !== 'login' && stage !== 'home' && error && <p className="alert" role="alert">{error}</p>}
      </main>
    </>
  );
}

function Landing({ list, draw, boxList, onStart }: { list: MemberRow[] | null; draw: DrawRow | null | undefined; boxList: BoxRow[]; onStart: () => void }) {
  const count = list?.length ?? 0;
  const notYet = NAMES.filter((n) => !boxList.some((b) => b.name === n));
  const status = draw ? 'Vylosováno! Otevři svůj dárek.' : count === NAMES.length ? 'Jsme všechny čtyři. Může se losovat!' : `Přihlášené: ${count} ze 4`;
  return (
    <>
      <header className="hero">
        <p className="kicker">Vánoce 2026</p>
        <h1><span>Tajný</span> Ježíšek</h1>
        <p className="names">Kamča · Lucík · Sjůzn · Katy</p>
      </header>
      <section className="card">
        <p className="status">{status}</p>
        <Roster list={list ?? []} />
        {draw && (
          <p className="fine looked">
            {notYet.length
              ? <>Na svůj dárek se ještě nepodívaly: <b>{notYet.join(', ')}</b>. Dokud to neudělají, jejich dárkyně nevidí adresu a přání.</>
              : <>Všechny už se podívaly na svůj dárek. Teď už jen nakoupit a zabalit!</>}
          </p>
        )}
        <button type="button" className="cta" onClick={onStart}>
          {draw ? 'Otevřít dárek' : count === NAMES.length ? 'Jdu losovat' : 'Jdu do toho'}
        </button>
      </section>
      <p className="hint">Rozpočet do {BUDGET} · dárky nejpozději {DEADLINE}</p>
    </>
  );
}

function Help({ onBack }: { onBack: () => void }) {
  return (
    <>
      <section className="card rules">
        <p className="kicker">Návod</p>
        <h2>Jak to funguje</h2>
        <ol>
          <li><b>Přihlas se.</b> Klepni na tlačítko <i>Jdu do toho</i> a vyber své jméno. Napiš, kam ti má dárek dorazit a co by ti udělalo radost. Zvol si tajné heslo a zapamatuj si ho.</li>
          <li><b>Počkej na ostatní.</b> Na úvodní stránce vidíš, které z nás už jsou přihlášené. Až tu budeme všechny čtyři, kterákoli z nás zmáčkne tlačítko <i>Vylosovat</i>. Stačí, když to udělá jedna.</li>
          <li><b>Rozbal dárek.</b> Přihlas se svým heslem a klepni na dárek. Dozvíš se, komu letos naděluješ.</li>
          <li><b>Podívej se na adresu a přání.</b> Adresa a přání té, které naděluješ, se objeví pod jejím jménem ve chvíli, kdy si ona sama rozbalí svůj dárek. Pokud tam ještě nejsou, připomeň ve skupině všem, ať si dárek rozbalí.</li>
          <li><b>Kup, zabal, pošli.</b> Rozpočet je do {BUDGET}. Dárek pošli nebo předej nejpozději {DEADLINE}</li>
        </ol>
      </section>
      <section className="card rules">
        <h2>Časté otázky</h2>
        <dl className="faq">
          <dt>Kdo vidí, komu naděluju?</dt>
          <dd>Jen ty. Kdo komu naděluje, adresy i přání jsou zamčené heslem každé z nás. Nevidí to nikdo jiný, ani Kamísek, která appku navajbila.</dd>
          <dt>Kdo uvidí moji adresu a přání?</dt>
          <dd>Jen ta, která ti naděluje, a to až poté, co si po losování rozbalíš svůj dárek. Proto se na něj podívej co nejdřív.</dd>
          <dt>Zapomněla jsem heslo.</dt>
          <dd>Heslo nejde obnovit, znáš ho jen ty. Napiš Kamískovi: tvoji přihlášku smaže a přihlásíš se znovu. Pokud už proběhlo losování, bude se losovat znovu.</dd>
          <dt>Můžu si změnit adresu nebo přání?</dt>
          <dd>V appce už ne. Když je to potřeba, napiš to do skupiny, ať to vědí všechny. Své dárkyni to napsat nemůžeš, protože nevíš, která to je.</dd>
          <dt>Na kterém telefonu to funguje?</dt>
          <dd>Na jakémkoli, stačí odkaz. Přihlásit se můžeš i z jiného telefonu nebo počítače, vždy svým jménem a heslem.</dd>
        </dl>
        <button type="button" className="cta" onClick={onBack}>Zpátky k Ježíškovi</button>
      </section>
    </>
  );
}

function RegisterForm({ name, busy, error, onSubmit, back }: { name: Name; busy: string; error: string; onSubmit: (f: Profile & { password: string }) => void; back: () => void }) {
  const [address, setAddress] = useState('');
  const [wish, setWish] = useState('');
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [local, setLocal] = useState('');
  function submit(e: FormEvent) {
    e.preventDefault();
    if (address.trim().length < 3) return setLocal('Napiš, kam má dárek dorazit.');
    if (!wish.trim()) return setLocal('Napiš aspoň jedno přání, ať má Ježíšek nápovědu.');
    if (password.length < 6) return setLocal('Heslo musí mít aspoň 6 znaků.');
    if (password !== again) return setLocal('Hesla se neshodují.');
    setLocal('');
    onSubmit({ address, wish, password });
  }
  return (
    <Card title={`Ahoj, ${VOCATIVE[name]}!`} kicker="Přihláška" back={back}>
      <form onSubmit={submit} className="form">
        <label>
          Kam ti má dárek dorazit
          <textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={3} maxLength={400} placeholder="Jméno, ulice, město, PSČ… nebo „předáme si osobně“" autoComplete="street-address" />
        </label>
        <label>
          Co by tě potěšilo
          <textarea value={wish} onChange={(e) => setWish(e.target.value)} rows={4} maxLength={600} placeholder="Nápady, barvy, co máš ráda, čemu se vyhnout…" />
        </label>
        <label>
          Tajné heslo
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" placeholder="aspoň 6 znaků" />
        </label>
        <label>
          Heslo ještě jednou
          <input type="password" value={again} onChange={(e) => setAgain(e.target.value)} autoComplete="new-password" />
        </label>
        <p className="fine">Heslem si pak otevřeš svůj dárek. Nikdo jiný ho nezná a nejde obnovit, tak si ho někam zapiš.</p>
        {(local || error) && <p className="alert" role="alert">{local || error}</p>}
        <button className="cta" disabled={!!busy}>{busy || 'Přihlásit se'}</button>
      </form>
    </Card>
  );
}

function LoginForm({ name, busy, error, onSubmit, back }: { name: Name; busy: string; error: string; onSubmit: (p: string) => void; back: () => void }) {
  const [password, setPassword] = useState('');
  return (
    <Card title={`Vítej zpátky, ${VOCATIVE[name]}`} kicker="Odemknout" back={back}>
      <form onSubmit={(e) => { e.preventDefault(); onSubmit(password); }} className="form">
        <label>
          Tvoje tajné heslo
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" autoFocus />
        </label>
        {error && <p className="alert" role="alert">{error}</p>}
        <button className="cta" disabled={!!busy || !password}>{busy || 'Odemknout'}</button>
      </form>
    </Card>
  );
}

function Home({ session, mine, list, draw, boxList, refreshBoxes, busy, error, onDraw, onLogout }: {
  session: Session; mine: MemberRow | null; list: MemberRow[]; draw: DrawRow | null | undefined; boxList: BoxRow[];
  refreshBoxes: () => Promise<void>; busy: string; error: string; onDraw: () => void; onLogout: () => void;
}) {
  const me = session.name;
  const [secret, setSecret] = useState<Sealed | null>(null);
  const [her, setHer] = useState<Profile | null>(null);
  const [gift, setGift] = useState<'idle' | 'shake' | 'open'>('idle');
  const [openErr, setOpenErr] = useState('');
  const deposited = useRef(false);
  const missing = NAMES.filter((n) => !list.some((m) => m.name === n));

  // Po losování: otevřít svůj balíček a vložit svou adresu do schránky pro dárkyni (jen jednou).
  useEffect(() => {
    if (!draw || !mine || deposited.current) return;
    deposited.current = true;
    (async () => {
      try {
        const s = JSON.parse(await openSealed(session.priv, draw[me])) as Sealed;
        setSecret(s);
        const already = await boxes.list({ filter: { name: me }, limit: 50 });
        if (already.records.length) return;
        const profile = await aesDecrypt(session.aes, mine.selfIv, mine.self);
        const box = await aesEncrypt(await importKeyB64(s.myKey), profile);
        await boxes.create({ name: me, iv: box.iv, data: box.data });
        await refreshBoxes();
      } catch (e) {
        deposited.current = false;
        setOpenErr(e instanceof DrobekError ? describeError(e) : 'Dárek se nepodařilo otevřít. Zkus se odhlásit a přihlásit znovu.');
      }
    })();
  }, [draw, mine]);

  // Schránka obdarované: první záznam, který jde otevřít naším klíčem.
  useEffect(() => {
    if (!secret) return;
    const candidates = boxList.filter((b) => b.name === secret.to);
    if (!candidates.length) { setHer(null); return; }
    let cancelled = false;
    (async () => {
      const key = await importKeyB64(secret.giveKey);
      for (const c of candidates) {
        try {
          const p = JSON.parse(await aesDecrypt(key, c.iv, c.data)) as Profile;
          if (!cancelled) setHer(p);
          return;
        } catch { /* cizí nebo poškozený záznam, zkusíme další */ }
      }
    })();
    return () => { cancelled = true; };
  }, [secret, boxList]);

  function reveal() {
    if (!secret) return;
    setGift('shake');
    setTimeout(() => {
      setGift('open');
      confetti({ particleCount: 160, spread: 80, origin: { y: 0.55 }, colors: ['#e8c46c', '#f6e2a6', '#c8343f', '#fbf3e2', '#2f7d5a'] });
      setTimeout(() => confetti({ particleCount: 80, angle: 60, spread: 60, origin: { x: 0, y: 0.7 }, colors: ['#e8c46c', '#c8343f', '#fbf3e2'] }), 250);
      setTimeout(() => confetti({ particleCount: 80, angle: 120, spread: 60, origin: { x: 1, y: 0.7 }, colors: ['#e8c46c', '#c8343f', '#fbf3e2'] }), 400);
    }, 900);
  }

  if (draw === undefined) return <Card title="Nahrávám…"><p className="fine">Chvilku…</p></Card>;

  if (!draw) {
    const ready = missing.length === 0;
    return (
      <Card title={ready ? 'Jsme tu všechny!' : 'Ještě chvilku'} kicker={`Přihlášená jako ${me}`}>
        <Roster list={list} />
        {ready ? (
          <>
            <p className="fine">Stačí, když to zmáčkne jedna z nás. Každá pak uvidí jen svůj dárek.</p>
            {error && <p className="alert" role="alert">{error}</p>}
            <button type="button" className="cta" onClick={onDraw} disabled={!!busy}>{busy || '🎲 Vylosovat'}</button>
          </>
        ) : (
          <p className="fine">Čekáme ještě na: <b>{missing.join(', ')}</b>. Připomeň to ve skupině a vrať se, až budeme všechny.</p>
        )}
        <button type="button" className="link" onClick={onLogout}>Odhlásit</button>
      </Card>
    );
  }

  return (
    <section className="card reveal">
      <p className="kicker">{VOCATIVE[me]}, tohle je jen pro tvoje oči</p>
      {gift !== 'open' && <h2>{secret ? 'Klepni na dárek' : 'Rozbaluju…'}</h2>}
      <Gift state={gift} onClick={secret ? reveal : undefined} />
      {openErr && <p className="alert" role="alert">{openErr}</p>}
      {gift === 'open' && secret && (
        <div className="result">
          <p className="lead">Letos naděluješ</p>
          <p className="target">{secret.to}</p>
          {her ? (
            <dl>
              <dt>Kam poslat</dt>
              <dd>{her.address}</dd>
              <dt>Co by ji potěšilo</dt>
              <dd>{her.wish}</dd>
              <dt>Rozpočet</dt>
              <dd>do {BUDGET}, nejpozději {DEADLINE}</dd>
            </dl>
          ) : (
            <p className="waiting">{secret.to} se ještě nepodívala na svůj dárek. Jakmile to udělá, objeví se tady její adresa a přání. Můžeš jí to pošeptat.</p>
          )}
          <p className="fine">Pšt. Tohle vidíš jen ty.</p>
        </div>
      )}
      <button type="button" className="link" onClick={onLogout}>Zavřít a odhlásit</button>
    </section>
  );
}

createRoot(document.getElementById('root')!).render(<App />);
